/**
 * Validate user access to the resource (for sharing)
 *
 * For now, we assume all users can access the resource.
 * In a real implementation, this would query the server to check permissions
 * based on the resource type and user's role/access level.
 */
export const validateUserAccess = (userId: string): boolean => {
  return true;
};

import type { TabItem } from "./types";

/**
 * Filter the saved-view tab pills down to what should render in the bar, honoring
 * the user's hide preference — while guaranteeing two UX invariants:
 *
 *  1. The "Main" tab (`id === null`) is NEVER hideable.
 *  2. The currently active view is ALWAYS shown, even if the user has it marked
 *     hidden — so applying a view (e.g. via URL/preset) can never leave the user
 *     staring at a bar that doesn't contain the view they're looking at.
 *
 * Pure function — no storage, no React. The hide source (`isHidden`) is injected
 * so this stays trivially testable.
 */
export const filterVisibleTabs = (
  tabs: TabItem[],
  isHidden: (viewId: string) => boolean,
  activeViewId: string | null
): TabItem[] =>
  tabs.filter(
    (tab) => tab.id === null || tab.id === activeViewId || !isHidden(tab.id)
  );

/**
 * Apply the user's saved left-to-right order preference to the tab list.
 *
 * Invariants:
 *  1. The "Main" tab (`id === null`) is NEVER reorderable — it is always pinned
 *     first, matching the fact that it is also never hideable.
 *  2. Views present in `orderedIds` sort by their stored index.
 *  3. Views absent from `orderedIds` (newly created, or a never-touched entity)
 *     keep their natural relative order and fall in *after* ordered views.
 *
 * Pure — no storage, no React — so it is trivially testable. `Array.sort` is
 * stable in modern engines, so unknown ids (index `Infinity`) retain order.
 */
export const orderTabs = (
  tabs: TabItem[],
  orderedIds: string[]
): TabItem[] => {
  const indexOf = (id: string): number => {
    const i = orderedIds.indexOf(id);
    return i === -1 ? Number.POSITIVE_INFINITY : i;
  };
  const main = tabs.filter((t) => t.id === null);
  const rest = tabs
    .filter((t): t is TabItem & { id: string } => t.id !== null)
    .sort((a, b) => indexOf(a.id) - indexOf(b.id));
  return [...main, ...rest];
};

/**
 * Merge a reorder of the *visible* subset back into the full canonical order,
 * leaving hidden views pinned in their existing slots.
 *
 * The tab bar only renders visible pills, so dragging there can only reorder
 * that subset. We keep the absolute positions of hidden ids intact and drop the
 * newly-ordered visible ids into the slots visible ids previously occupied.
 */
export const mergeVisibleReorder = (
  fullOrder: string[],
  newVisibleOrder: string[]
): string[] => {
  const visibleSet = new Set(newVisibleOrder);
  const queue = [...newVisibleOrder];
  return fullOrder.map((id) => (visibleSet.has(id) ? (queue.shift() as string) : id));
};
